import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { DatabaseBackup, Download, Loader2, ShieldCheck, CheckCircle2, Building2 } from "lucide-react";

function fmtSize(b) {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

export default function Backups() {
  const [ctx, setCtx] = useState(null);
  const [scope, setScope] = useState("organization");
  const [account, setAccount] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [org, setOrg] = useState("");
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);

  // Load context (role, permitted accounts).
  useEffect(() => {
    api.get("/backups/context").then((r) => {
      setCtx(r.data);
      setScope(r.data.can_account_backup ? "account" : "organization");
      setAccount(r.data.default_account_id || (r.data.accounts?.[0]?.id) || "");
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  // Load permitted orgs whenever account changes (used by org scope).
  useEffect(() => {
    if (!ctx) return;
    api.get("/backups/orgs", { params: { account_id: account } }).then((r) => {
      setOrgs(r.data.organizations || []);
      setOrg((prev) => (r.data.organizations || []).some((o) => o.id === prev) ? prev
        : (r.data.organizations?.length === 1 ? r.data.organizations[0].id : ""));
    }).catch(() => setOrgs([]));
    setResult(null);
  }, [ctx, account]);

  const create = async () => {
    if (scope === "account" && !account) return toast.error("Select an account");
    if (scope === "organization" && !org) return toast.error("Select an organization");
    setCreating(true); setResult(null);
    try {
      const { data } = await api.post("/backups/create", {
        scope, account_id: account, organization_id: scope === "organization" ? org : undefined,
      });
      setResult(data);
      toast.success("Backup created");
    } catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };

  const download = async () => {
    if (!result) return;
    setDownloading(true);
    try {
      const resp = await api.get(`/backups/download/${result.backup_id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = result.filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
      setResult(null);
    } catch (e) { toast.error(apiError(e)); } finally { setDownloading(false); }
  };

  const nonzero = result ? Object.entries(result.collections).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]) : [];
  const hideOrgSelector = scope === "organization" && orgs.length === 1;

  return (
    <div data-testid="backups-page" className="max-w-3xl">
      <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
        <DatabaseBackup className="w-6 h-6 text-[#0D9488]" /> Maintenance · Data Backups
      </h1>
      <p className="text-sm text-slate-500 mt-1">
        Create a downloadable, timestamped logical backup (ZIP of JSON). Export-only — this never changes your data.
        Credentials, OAuth tokens, password hashes and secrets are never included.
      </p>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          {ctx?.can_account_backup && (
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-medium text-slate-500">Backup scope</label>
              <Select value={scope} onValueChange={(v) => { setScope(v); setResult(null); }}>
                <SelectTrigger className="w-44" data-testid="backup-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="account">Entire Account</SelectItem>
                  <SelectItem value="organization">Single Organization</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {ctx?.can_account_backup && (
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-medium text-slate-500">Account</label>
              <Select value={account} onValueChange={(v) => { setAccount(v); setResult(null); }}>
                <SelectTrigger className="w-64" data-testid="backup-account-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {(ctx.accounts || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {scope === "organization" && !hideOrgSelector && (
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-medium text-slate-500">Organization</label>
              <Select value={org} onValueChange={(v) => { setOrg(v); setResult(null); }}>
                <SelectTrigger className="w-64" data-testid="backup-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {hideOrgSelector && (
            <div className="flex items-center gap-1.5 text-sm text-slate-600 pb-1.5" data-testid="backup-org-single">
              <Building2 className="w-4 h-4 text-slate-400" /> {orgs[0]?.name}
            </div>
          )}

          <Button onClick={create} disabled={creating || (scope === "organization" && !org)} data-testid="backup-create-btn" className="bg-[#006090] hover:bg-[#004d73]">
            {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : <><DatabaseBackup className="w-4 h-4 mr-2" /> Create Data Backup</>}
          </Button>
        </div>

        <div className="flex items-start gap-2 text-xs text-slate-500">
          <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
          Attachment metadata is included; file binaries are not (they stay in durable object storage).
          {!ctx?.can_account_backup && " You can back up only the organization(s) you administer."}
        </div>
      </div>

      {result && (
        <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50/50 p-5" data-testid="backup-result">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="w-5 h-5" /> Backup ready <span className="text-xs font-normal text-slate-500">({result.scope})</span></div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Stat label="Filename" value={result.filename} testid="backup-filename" wide />
            <Stat label="Size" value={fmtSize(result.size_bytes)} testid="backup-size" />
            <Stat label="Total records" value={result.total_records.toLocaleString()} testid="backup-total" />
            <Stat label="Created (UTC)" value={result.manifest?.backup_timestamp_utc?.slice(0, 19).replace("T", " ")} />
          </div>
          <div className="mt-4">
            <Button onClick={download} disabled={downloading} data-testid="backup-download-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {downloading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</> : <><Download className="w-4 h-4 mr-2" /> Download ZIP</>}
            </Button>
            <span className="ml-3 text-xs text-slate-400">One-time download; the server copy is removed afterward.</span>
          </div>
          <details className="mt-4 text-sm">
            <summary className="cursor-pointer text-slate-600 font-medium">Record counts by collection ({nonzero.length} with data)</summary>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1" data-testid="backup-counts">
              {nonzero.map(([k, v]) => <div key={k} className="flex justify-between border-b border-slate-100 py-0.5"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v.toLocaleString()}</span></div>)}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, testid, wide }) => (
  <div className={wide ? "col-span-2" : ""}>
    <div className="text-[11px] text-slate-500">{label}</div>
    <div className="font-medium text-slate-900 break-all" data-testid={testid}>{value}</div>
  </div>
);
